"use client";

import Section from "../../../components/common/Section";
import Input from "../../../components/Input";
import { FieldErrors, UseFormRegister, UseFormReset } from "react-hook-form";
import SelectInput from "../../../components/Select";
import {
  useFetchNaqelCredentials,
  useFetchAramexCredentials,
  useFetchZidCredentials,
  useFetchJtCredentials,
} from "../../../apis/integrationApis";
import { useEffect } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import { IconApi, IconSettings, IconSettingsCode } from "@tabler/icons-react";

type FormValues = {
  accountNumber?: string;
  authKey?: string;
  clientId?: string;
  secretKey: string;
  appId: string;
  serviceCode?: string;
  ispudo?: string;
  shopDomain?: string;
  accessToken?: string;
  apiVersion?: string;
  // Naqel fields
  userName?: string;
  password?: string;
  customerCode?: string;
  branchCode?: string;
  //Aramex Fields
  accountPin?: string;
  accountEntity?: string;
  accountCountryCode?: string;
  paymentType?: string;
  productGroup?: string;
  productType?: string;
  //ZID
  clientSecret?: string;
  // J&T
  apiAccount?: string;
  privateKey?: string;
};

type Props = {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  tab: string;
  reset: UseFormReset<FormValues>;
  channelType: string;
  clientId: string;
};

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

const EditViewCredentialsModal = ({
  register,
  errors,
  tab,
  channelType,
  reset,
  clientId,
}: Props) => {
  const { t } = useLanguage();
  console.log("EditViewCredentialsModal props:", {
    register,
    errors,
    tab,
    channelType,
  });

  const shippingFields = [
    {
      name: "authKey" as const,
      title: "Auth Key",
      placeholder: "******",
      validation: { required: "Auth key is required" },
      disabled: false,
    },
  ];

  const otherFields = [
    {
      name: "clientId" as const,
      title: "Client ID",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "Client ID is required" },
      disabled: false,
    },
    {
      name: "secretKey" as const,
      title: "Secret Key",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "Secret key is required" },
      disabled: false,
    },
    {
      name: "appId" as const,
      title: "App ID",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "App ID is required" },
      disabled: false,
    },
  ];

  const connectionFields = [
    {
      name: "clientId" as const,
      title: "Client ID",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "Client ID is required" },
      disabled: false,
    },
    {
      name: "secretKey" as const,
      title: "Secret Key",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "Secret key is required" },
      disabled: false,
    },
  ];

  const shoapifyFields = [
    {
      name: "shopDomain" as const,
      title: "Shop Domain",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "Shop Domain is required" },
      disabled: false,
    },
    {
      name: "appId" as const,
      title: "API Key",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "Api Key is required" },
      disabled: false,
    },
    {
      name: "secretKey" as const,
      title: "API Secret Key",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "API Secret Key is required" },
      disabled: false,
    },
    {
      name: "accessToken" as const,
      title: "Access Token",
      placeholder: "s3gh5hbhv6***********",
      validation: { required: "Access Token is required" },
      disabled: false,
    },
    {
      name: "apiVersion" as const,
      title: "API Version",
      placeholder: "2024-07",
      validation: { required: "API Version is required" },
      disabled: false,
    },
  ];

  // ── Naqel fields ────────────────────────────────────────────────────────────
  const naqelFields = [
    {
      name: "userName" as const,
      title: "Username",
      placeholder: "Enter username",
      validation: { required: "Username is required" },
      disabled: false,
    },
    {
      name: "password" as const,
      title: "Password",
      placeholder: "Leave blank to keep existing",
      validation: {}, // optional on edit
      disabled: false,
    },
    {
      name: "customerCode" as const,
      title: "Customer Code",
      placeholder: "e.g. NL123456",
      validation: { required: "Customer Code is required" },
      disabled: false,
    },
    {
      name: "branchCode" as const,
      title: "Branch Code",
      placeholder: "e.g. NL567899",
      validation: { required: "Branch Code is required" },
      disabled: false,
    },
  ];

  // ── Aramex fields ────────────────────────────────────────────────────────────
  const aramexFields = [
    {
      name: "accountNumber" as const,
      title: "Account Number",
      placeholder: "784*****",
      validation: { required: "Account number is required" },
      disabled: false,
    },
    {
      name: "accountPin" as const,
      title: "Account Pin",
      placeholder: "36****",
      validation: { required: "Account Pin is required" },
      disabled: false,
    },
    {
      name: "userName" as const,
      title: "Username",
      placeholder: "Enter Username",
      validation: { required: "Username is required" },
      disabled: false,
    },
    {
      name: "password" as const,
      title: "Password",
      placeholder: "Leave blank to keep existing",
      validation: {},
      disabled: false,
    },
    {
      name: "accountEntity" as const,
      title: "Account Entity",
      placeholder: "e.g. DHA",
      validation: { required: "Account Entity is required" },
      disabled: false,
    },
    {
      name: "accountCountryCode" as const,
      title: "Account Country Code",
      placeholder: "e.g. SA",
      validation: { required: "Account Country Code is required" },
      disabled: false,
    },
    {
      name: "paymentType" as const,
      title: "Payment Type",
      placeholder: "P",
      validation: { required: "Payment Type is required" },
      disabled: false,
    },
    {
      name: "productGroup" as const,
      title: "Product Group",
      placeholder: "DOM",
      validation: { required: "Product Group is required" },
      disabled: false,
    },
    {
      name: "productType" as const,
      title: "Product Type",
      placeholder: "CDS",
      validation: { required: "Product Type is required" },
      disabled: false,
    },
  ];

  // ZID Fields ───────────────────────────────────────────────────
  const zidFields = [
    {
      name: "clientId" as const,
      title: "Client ID",
      placeholder: "eg 1234",
      validation: { required: "Client ID is required" },
      disabled: false,
    },
    {
      name: "clientSecret" as const,
      title: "Client Secret",
      placeholder: "eg s3gh5hbhv6***********",
      validation: { required: "Client secret is required" },
      disabled: false,
    },
    {
      name: "appId" as const,
      title: "App ID",
      placeholder: "eg 1234",
      validation: { required: "App ID is required" },
      disabled: false,
    },
  ];

  // J&T Fields ───────────────────────────────────────────────────
  const jtFields = [
    {
      name: "customerCode" as const,
      title: "Customer Code",
      placeholder: "Enter customer code",
      validation: { required: "Customer Code is required" },
      disabled: false,
    },
    {
      name: "apiAccount" as const,
      title: "API Account",
      placeholder: "Enter API account",
      validation: { required: "API Account is required" },
      disabled: false,
    },
    {
      name: "privateKey" as const,
      title: "Private Key",
      placeholder: "Leave blank to keep existing",
      validation: {},
      disabled: false,
    },
  ];

  // ── Field selection logic ───────────────────────────────────────────────────
  // Use .includes() to match "Naqel", "Naqel Express", "Aramex" etc.
  const channelLower = channelType.toLowerCase();
  const isJtChannel = channelLower.includes("j&t") || channelLower.includes("jt express");
  const fields = channelLower.includes("naqel")
    ? naqelFields
    : channelLower.includes("aramex")
      ? aramexFields
      : channelLower.includes("zid")
        ? zidFields
        : isJtChannel
          ? jtFields
          : tab === "Shipping"
            ? shippingFields
            : tab === "Connection Account"
              ? connectionFields
              : channelType === "Shoapify"
                ? shoapifyFields
                : otherFields;

  const { data: naqelData } = useFetchNaqelCredentials(channelType, clientId);

  useEffect(() => {
    // Only reset for Naqel — never let Naqel cache overwrite other forms
    if (naqelData && channelLower.includes("naqel")) {
      reset({
        userName: naqelData?.userName || "",
        customerCode: naqelData?.customerCode || "",
        branchCode: naqelData?.branchCode || "",
        password: "",
      });
    }
  }, [naqelData, channelType, reset, channelLower]);

  const { data: aramexData } = useFetchAramexCredentials(channelType, clientId);

  useEffect(() => {
    // Only reset for Aramex — never let Aramex cache overwrite other forms
    if (aramexData && channelLower.includes("aramex")) {
      reset({
        userName: aramexData?.userName || "",
        accountNumber: aramexData?.accountNumber || "",
        accountPin: aramexData?.accountPin || "",
        accountEntity: aramexData?.accountEntity || "",
        accountCountryCode: aramexData?.accountCountryCode || "",
        paymentType: aramexData?.paymentType || "",
        productGroup: aramexData?.productGroup || "",
        productType: aramexData?.productType || "",
        password: "",
      });
    }
  }, [aramexData, channelType, reset, channelLower]);

  const { data: zidData } = useFetchZidCredentials(channelType, clientId);

  useEffect(() => {
    // Only reset for ZID — never let ZID cache overwrite other forms
    if (zidData && channelLower.includes("zid")) {
      reset({
        clientId: zidData?.zidClientId || "",
        clientSecret: zidData?.zidClientSecret || "",
        appId: zidData?.zidAppId || "",
      });
    }
  }, [zidData, channelType, reset, channelLower]);

  const { data: jtData } = useFetchJtCredentials(channelType, clientId);

  useEffect(() => {
    // Keep private key blank on edit so saved secret is preserved unless replaced.
    if (jtData && isJtChannel) {
      reset({
        customerCode: jtData?.customerCode || "",
        apiAccount: jtData?.apiAccount || "",
        privateKey: "",
      });
    }
  }, [jtData, reset, isJtChannel]);

  return (
    <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
      <SectionHeader icon={IconSettingsCode} title="API Access Information" />
      <div className="grid gap-4">
        {fields.map(({ name, title, placeholder, validation, disabled }) => (
          <Input
            className="flex items-center justify-between"
            labelClass="w-full"
            key={name}
            name={name}
            type={name === "password" || name === "privateKey" ? "password" : "text"}
            title={t(title)}
            placeholder={placeholder ? t(placeholder) : ""}
            register={register}
            other={{
              className:
                "px-4 py-2 text-sm w-full rounded-full outline-none bg-white border border-[#D8D8D8]",
            }}
            disabled={disabled}
            error={errors[name]}
            validation={validation ? { ...validation, required: typeof validation.required === 'string' ? t(validation.required) : validation.required } : undefined}
          />
        ))}
      </div>

      {tab === "Shipping" && (
        <div className="space-y-3">
          <SectionHeader icon={IconSettings} title="Advance Settings" />
          <div className="grid gap-4">
            <Input
              className="flex items-center justify-between"
              labelClass="w-full"
              type="text"
              title={t("Service Code")}
              placeholder=""
              register={register}
              name="serviceCode"
              error={errors.serviceCode}
              other={{
                className:
                  "px-4 py-2 text-sm w-full rounded-full outline-none bg-white border border-[#D8D8D8]",
              }}
            />
            <SelectInput
              className="flex items-center justify-between"
              labelClass="w-full"
              label={t("isPudo")}
              selectOptions={[
                { value: "Yes", text: t("Yes") },
                { value: "No", text: t("No") },
              ]}
              register={register}
              name="ispudo"
              error={errors.ispudo}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EditViewCredentialsModal;
